import { FC, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DropdownButton, DropdownOption } from '@dbt-labs/sourdough';

import { PackageResourceType } from '@/__generated__/graphql';
import { useAppliedPackageNames } from '@/hooks/discovery/appliedPackageNames';

export type PackageFilterDropdownProps = {
  environmentId: number;
  onLoad?: () => void;
  resourceType: PackageResourceType;
};

export const ALL_PACKAGES_VALUE = '__all__';
export const PACKAGE_NAME_PARAM = 'packageName';

export const PackageFilterDropdown: FC<PackageFilterDropdownProps> = ({
  environmentId,
  onLoad: loaded,
  resourceType,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const packageInfo = useAppliedPackageNames({
    environmentId,
    resourceType,
  });

  // If the current package does not contain any resources, default to all packages
  let currentPackageName = packageInfo.current;
  if (currentPackageName && !packageInfo.packages?.includes(currentPackageName)) {
    currentPackageName = ALL_PACKAGES_VALUE;
  }
  const urlPackageName = searchParams.get(PACKAGE_NAME_PARAM);

  useEffect(() => {
    if (!urlPackageName && currentPackageName) {
      setSearchParams((prev) => {
        prev.set(PACKAGE_NAME_PARAM, currentPackageName!);
        return prev;
      });
    }
  }, [currentPackageName, setSearchParams, urlPackageName]);

  const defaultPackage =
    searchParams.get(PACKAGE_NAME_PARAM) || packageInfo.current || ALL_PACKAGES_VALUE;

  const packageOptions = useMemo(() => {
    const opts = [
      { label: 'All', value: ALL_PACKAGES_VALUE },
      ...(packageInfo?.packages?.map<DropdownOption>((name) => ({
        label: name,
        value: name,
      })) || []),
    ];

    return opts;
  }, [packageInfo?.packages]);

  const hasPackageLoaded = packageInfo?.packages !== null || !packageInfo.fetching;
  useEffect(() => {
    if (loaded && hasPackageLoaded) {
      loaded();
    }
  }, [loaded, hasPackageLoaded]);

  return (
    <DropdownButton
      options={packageOptions}
      name={'Package:'}
      listClassName="overflow-visible z-50"
      defaultOption={packageOptions.find((o) => o.value === defaultPackage)}
      trackingId="explorer-package-filter"
      onChange={({ value }) => {
        if (typeof value !== 'string') return;
        setSearchParams((p) => {
          p.set(PACKAGE_NAME_PARAM, value);
          return p;
        });
      }}
    />
  );
};
